/**
 * The only module that touches the DOM. Keeping every query and mutation behind
 * this interface means the controller can be tested with a plain object, and a
 * future framework rewrite replaces one file.
 */

export type ViewState =
  | { readonly kind: "idle" }
  | { readonly kind: "submitting" }
  | { readonly kind: "result"; readonly prompt: string }
  | { readonly kind: "error"; readonly message: string };

export interface View {
  /** Current text in the prompt input. */
  getPromptText(): string;
  render(state: ViewState): void;
  onSubmit(handler: () => void): void;
}

export type Elements = {
  readonly form: HTMLFormElement;
  readonly input: HTMLTextAreaElement;
  readonly button: HTMLButtonElement;
  readonly output: HTMLElement;
  readonly status: HTMLElement;
};

const MISSING_ELEMENT = (id: string): Error =>
  new Error(`Expected an element with id '${id}' in the document`);

/**
 * Resolves the elements the view needs.
 *
 * @throws {Error} when the document does not contain the expected markup, so a
 * broken template fails loudly at startup instead of silently doing nothing.
 */
export function resolveElements(document: Document): Elements {
  const form = document.getElementById("prompt-form");
  const input = document.getElementById("prompt-input");
  const button = document.getElementById("submit-button");
  const output = document.getElementById("output");
  const status = document.getElementById("status");

  if (form === null) throw MISSING_ELEMENT("prompt-form");
  if (input === null) throw MISSING_ELEMENT("prompt-input");
  if (button === null) throw MISSING_ELEMENT("submit-button");
  if (output === null) throw MISSING_ELEMENT("output");
  if (status === null) throw MISSING_ELEMENT("status");

  return {
    form: form as HTMLFormElement,
    input: input as HTMLTextAreaElement,
    button: button as HTMLButtonElement,
    output,
    status,
  };
}

export class DomView implements View {
  constructor(private readonly elements: Elements) {}

  getPromptText(): string {
    return this.elements.input.value;
  }

  onSubmit(handler: () => void): void {
    this.elements.form.addEventListener("submit", (event) => {
      event.preventDefault();
      handler();
    });
  }

  render(state: ViewState): void {
    const { button, output, status } = this.elements;

    button.disabled = state.kind === "submitting";

    switch (state.kind) {
      case "idle":
        status.textContent = "";
        status.dataset.tone = "neutral";
        output.textContent = "";
        output.hidden = true;
        return;
      case "submitting":
        status.textContent = "Sending…";
        status.dataset.tone = "neutral";
        return;
      case "result":
        status.textContent = "";
        status.dataset.tone = "neutral";
        // textContent, never innerHTML: prompt text is untrusted input and must
        // never be parsed as markup.
        output.textContent = state.prompt;
        output.hidden = false;
        return;
      case "error":
        status.textContent = state.message;
        status.dataset.tone = "error";
        output.hidden = true;
        return;
    }
  }
}
